import { inject } from '@angular/core';
import {
  CanActivateFn,
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { Route } from '../enums/route.enum';
import { UserRole } from '../enums/user-roles.enum';
import { PortalRole, normalizePortalRole, UserService } from '../../services/user/user.service';
import {
  pickFirstAccessibleRoutePath,
  routePriorityForRoleLanding,
} from '../utils/route-access.util';
import { PartySearchContextService } from '../../services/party-search/party-search-context.service';

/** Customer pages internal users may open while impersonating via party search. */
const IMPERSONATION_CUSTOMER_ROUTES = new Set<string>([
  Route.FAQ,
  Route.CONTACT_US,
  Route.CUSTOMER_REQUEST_CHANGE,
  Route.CUSTOMER_FEEDBACK_USER,
]);

function getAllowedRoles(route: ActivatedRouteSnapshot): PortalRole[] {
  const raw = route.data?.['allowedRoles'];
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item) => normalizePortalRole(item))
    .filter((role): role is PortalRole => role !== null);
}

function canAccessViaPartyImpersonation(
  loggedInRole: PortalRole,
  route: ActivatedRouteSnapshot,
  partySearchContext: PartySearchContextService,
): boolean {
  const isInternal = loggedInRole === UserRole.Admin || loggedInRole === UserRole.CNHUser;
  if (!isInternal) {
    return false;
  }

  const partyActive =
    partySearchContext.params() !== null && partySearchContext.contractData() !== null;
  if (!partyActive) {
    return false;
  }

  const path = route.routeConfig?.path;
  return !!path && IMPERSONATION_CUSTOMER_ROUTES.has(path);
}

export const roleGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot,
) => {
  const router = inject(Router);
  const userService = inject(UserService);
  const partySearchContext = inject(PartySearchContextService);

  const allowedRoles = getAllowedRoles(route);
  if (allowedRoles.length === 0) {
    return true;
  }

  return userService.getUserRoleGate$().pipe(
    map((role) => {
      if (allowedRoles.includes(role)) {
        return true;
      }

      if (canAccessViaPartyImpersonation(role, route, partySearchContext)) {
        return true;
      }

      const partySearchActive = partySearchContext.params() !== null;
      const fallback = pickFirstAccessibleRoutePath(
        role,
        router.config,
        routePriorityForRoleLanding(role),
        partySearchActive,
      );
      return router.parseUrl(fallback ? `/${fallback}` : `/${Route.LOGIN}`);
    }),
    catchError(() => of(router.parseUrl(`/${Route.LOGIN}`))),
  );
};
